import { memo, useEffect, useState, useCallback } from 'react';
import { Stack, Typography, Card, CardContent, Icon } from 'investira.react.components';
import services from '../../../services';
import { METRICAS_TIPO } from '../../../components/organisms/EmpresaMetricasInfo/FilterMetricasEmpresa';
import EmpresasMetricas from '../../../components/organisms/EmpresaMetricasInfo/EmpresasMetricas';
import PatrimonioCard from '../../../components/organisms/EmpresaIndicadores/PatrimonioCard';
import CapitalSocialCard from '../../../components/organisms/EmpresaIndicadores/CapitalSocialCard';

const RankingCard = memo(
  ({
    title,
    data,
    isLoading,
    iconName,
    sx,
    filters,
    onFilterChange,
    onClick,
    selectedId,
    onPageChange
  }) => {
    const header = (
      <Stack direction="row" alignItems="center" spacing={1}>
        <Icon iconName={iconName} size={24} color="primary" />
        <Typography variant="subtitle1" color="textPrimary" sx={{ fontWeight: 500 }} component="h3">
          {title}
        </Typography>
      </Stack>
    );

    return (
      <Card sx={{ ...sx }}>
        <CardContent>
          <EmpresasMetricas
            isLoading={isLoading}
            data={data}
            onClick={onClick}
            filters={filters}
            onChangeFilter={onFilterChange}
            pageSize={6}
            showFilter={true}
            showPagination={true}
            header={header}
            filterConfig={{ showFaixa: false, showSimilaridade: false }}
            showSectors={false}
            selectedId={selectedId}
            onPageChange={onPageChange}
          />
        </CardContent>
      </Card>
    );
  }
);

RankingCard.displayName = 'RankingCard';

const Empresas = memo(() => {
  const [filters, setFilters] = useState({ metrica: 'pl' });
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedEmpresa, setSelectedEmpresa] = useState(null);

  const handleFilterChange = useCallback(pNewFilters => {
    setFilters(prev => ({ ...prev, ...pNewFilters }));
  }, []);

  useEffect(() => {
    setSelectedEmpresa(null);
  }, [filters.metrica]);

  useEffect(() => {
    const processData = (res, pMetrica) => {
      const pData = res.data || {};
      const pList = Array.isArray(pData) ? pData : Object.values(pData);
      pList.sort((a, b) => (b.metrica?.[pMetrica] || 0) - (a.metrica?.[pMetrica] || 0));
      return pList;
    };

    const fetchList = pMetrica => {
      setIsLoading(true);
      services.pessoas.listSemelhantes(
        {
          metrica: pMetrica
        },
        res => {
          setData(processData(res, pMetrica));
        },
        err => {
          console.error(err);
          setIsLoading(false);
        },
        () => {
          setIsLoading(false);
        }
      );
    };

    fetchList(filters.metrica);
  }, [filters.metrica]);

  useEffect(() => {
    if (!selectedEmpresa && data?.length) {
      setSelectedEmpresa(data[0]);
    }
  }, [data, selectedEmpresa]);

  const handleSelectEmpresa = useCallback((e, pItem) => {
    e?.preventDefault && e.preventDefault();
    setSelectedEmpresa(pItem);
  }, []);

  const handlePageChange = useCallback(pFirstItem => {
    setSelectedEmpresa(pFirstItem);
  }, []);

  const currentMetricInfo = Object.values(METRICAS_TIPO).find(m => m.id === filters.metrica);

  return (
    <Stack spacing={2}>
      <Typography variant="h6" color="textPrimary">
        Métricas de Empresas
      </Typography>
      <Stack
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(1, 1fr)', md: 'repeat(1, 1fr)', lg: 'repeat(3, 1fr)' },
          gap: 1.5
        }}>
        <RankingCard
          title={currentMetricInfo?.descricao || 'Métrica'}
          data={data}
          isLoading={isLoading}
          iconName="coin_plus"
          filters={filters}
          onFilterChange={handleFilterChange}
          onClick={handleSelectEmpresa}
          selectedId={selectedEmpresa?.pessoa_id}
          onPageChange={handlePageChange}
        />

        <Stack spacing={1.5} display="flex" direction="column">
          <PatrimonioCard
            pessoaId={selectedEmpresa?.pessoa_id}
            patrimonioLiquido={selectedEmpresa?.metrica?.pl}
            showChart={true}
            showHistoryButton={false}
          />
          <CapitalSocialCard
            pessoaId={selectedEmpresa?.pessoa_id}
            capitalSocial={selectedEmpresa?.metrica?.cs}
            showChart={true}
            showHistoryButton={false}
          />
        </Stack>

        {/* <Stack spacing={1.5} display="flex" direction="column">
          <PatrimonioCard
            pessoaId={selectedEmpresa?.pessoa_id}
            patrimonioLiquido={selectedEmpresa?.metrica?.pl}
            showChart={true}
            showHistoryButton={false}
          />
          <CapitalSocialCard
            pessoaId={selectedEmpresa?.pessoa_id}
            capitalSocial={selectedEmpresa?.metrica?.cs}
            showChart={true}
            showHistoryButton={false}
          />
        </Stack> */}
      </Stack>
    </Stack>
  );
});

Empresas.displayName = 'DashboardInfoEmpresas';

export default Empresas;
